import { EditorState, EditorThemeClasses, Klass, LexicalEditor, LexicalNode, LexicalNodeReplacement } from 'lexical'
import React, { ErrorInfo, ForwardRefExoticComponent, PropsWithChildren, PropsWithoutRef, ReactNode, RefAttributes } from 'react'

import { InitialEditorStateType, LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { defaultLexicalTheme } from '../defaultLexicalTheme'
import { getInitialLexicalEditorState } from '../utils'

import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ErrorBoundary } from 'react-error-boundary'

const onLexicalError = (error: unknown) => {
  console.error(error)
}

export interface ReadOnlyEditorProps {
  initialEditorState?: InitialEditorStateType
  initialHtmlValue?: string
  initialTextValue?: string
  onChange?: (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void
  theme?: EditorThemeClasses
  lexicalNodes?: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>
  onGeneralError?: (error: Error, info: ErrorInfo) => void
  errorFallback?: ReactNode
}

export const ReadOnlyLexicalEditor: ForwardRefExoticComponent<PropsWithoutRef<PropsWithChildren<ReadOnlyEditorProps>> & RefAttributes<LexicalEditor>> =
  React.forwardRef<LexicalEditor, PropsWithChildren<ReadOnlyEditorProps>>(
    ({ initialEditorState, initialHtmlValue, initialTextValue, onChange, theme, lexicalNodes, onGeneralError, errorFallback = null, children }, ref) => {
      const initialConfig = {
        editable: false,
        namespace: 'ReadOnlyLexicalEditor',
        theme: theme ?? defaultLexicalTheme,
        onError: onLexicalError,
        editorState: getInitialLexicalEditorState(initialEditorState, initialHtmlValue, initialTextValue),
        nodes: lexicalNodes ?? [],
      }

      return (
        <ErrorBoundary fallback={errorFallback} onError={onGeneralError}>
          <LexicalComposer initialConfig={initialConfig}>
            <RichTextPlugin contentEditable={<ContentEditable />} placeholder={<></>} ErrorBoundary={LexicalErrorBoundary} />
            {onChange && <OnChangePlugin onChange={onChange} ignoreSelectionChange />}
            {ref && <EditorRefPlugin editorRef={ref} />}
            <ListPlugin />
            <LinkPlugin />
            {children}
          </LexicalComposer>
        </ErrorBoundary>
      )
    },
  )
